// WCAG 2.x contrast ratio helpers. Used by the sportsbook editor to flip
// active-highlight text to whichever of black/white reads better against
// the operator's brand color.

function parseHex(hex: string): [number, number, number] | null {
  const s = hex.trim().replace(/^#/, '')
  if (s.length === 3) {
    const r = Number.parseInt(s[0] + s[0], 16)
    const g = Number.parseInt(s[1] + s[1], 16)
    const b = Number.parseInt(s[2] + s[2], 16)
    if ([r, g, b].some(Number.isNaN)) return null
    return [r, g, b]
  }
  if (s.length === 6) {
    const r = Number.parseInt(s.slice(0, 2), 16)
    const g = Number.parseInt(s.slice(2, 4), 16)
    const b = Number.parseInt(s.slice(4, 6), 16)
    if ([r, g, b].some(Number.isNaN)) return null
    return [r, g, b]
  }
  return null
}

// sRGB → linear-light, then weighted sum. Per WCAG 2.x definition.
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

// True for colors that are effectively black (#000–#0a-ish). Used by
// the editor to skip the brand override on near-black accents — those
// books fall back to the default --ink/--paper theme, which flips to
// off-white in dark mode so the active-state highlight stays visible.
export function isEffectivelyBlack(hex: string): boolean {
  const parsed = parseHex(hex)
  if (!parsed) return false
  return relativeLuminance(parsed) < 0.005
}

export function contrastRatio(hex1: string, hex2: string): number | null {
  const a = parseHex(hex1)
  const b = parseHex(hex2)
  if (!a || !b) return null
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  const [lighter, darker] = la >= lb ? [la, lb] : [lb, la]
  return (lighter + 0.05) / (darker + 0.05)
}

// Brand-driven overrides: books that should render white text on their
// accent regardless of which color the WCAG check prefers. These are
// operator-style decisions (DraftKings wears its green with white text
// on its own site, etc.) — preserved here so the editor accent matches
// the operator's actual brand treatment.
const FORCE_WHITE_TEXT = new Set([
  'draftkings',
  'caliente',
  'betcris',
])

// Pick whichever of #000 / #fff reads better against the given background.
// `bookName` (case-insensitive) lets brand-driven overrides force white
// when the operator's own brand guidance disagrees with raw WCAG.
// Returns null if the input isn't a parseable hex — caller falls back to
// the default neutral theme.
export function pickReadableText(bg: string, bookName?: string): '#000000' | '#ffffff' | null {
  if (bookName && FORCE_WHITE_TEXT.has(bookName.trim().toLowerCase())) {
    return parseHex(bg) ? '#ffffff' : null
  }
  const black = contrastRatio(bg, '#000000')
  const white = contrastRatio(bg, '#ffffff')
  if (black == null || white == null) return null
  return black >= white ? '#000000' : '#ffffff'
}
